import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { Trans } from '@lingui/macro'
import { FC, useState } from 'react'
import { ContactFields } from './ContactFields'
import { isNil } from 'lodash'
import ContactSearchResultCard from './ContactSearchResultCard'
import { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import Loading from 'egret/components/EgretLoadable/Loading'
import CustomButton from '../CustomButton'
import { ContactDialogType } from './useContactDialog'

interface ContactFieldsSearchResultsProps {
  loading: boolean
  searchResult: ContactParsed[]
  preview: boolean
  onConfirmSelection?: ({ contact, isValid }: { contact: ContactParsed, isValid: boolean }) => void
  onModifySearch?: () => void
  onCreateNewContact?: () => void
  type?: ContactDialogType
  role?: string
}

export const ContactFieldsSearchResults: FC<ContactFieldsSearchResultsProps> = ({
  loading,
  searchResult,
  preview,
  onConfirmSelection,
  onModifySearch,
  onCreateNewContact,
  type,
  role
}) => {
  const isResults = !isNil(searchResult) && Array.isArray(searchResult)
  const isSingleResult = isResults && searchResult.length === 1
  const [selectedContactId, setSelectedContactId] = useState<ContactParsed['id']>(isSingleResult ? searchResult?.[0]?.id : undefined)
  const [contactConsentConfirmed, setContactConsentConfirmed] = useState(false)

  return (
    <ContactFields
      hiddenFields={{
        providedOrganizationName: true,
        jobTitle: true,
        phoneNumber: true,
        phoneNumberCountryCode: true,
        email: true,
        firstName: true,
        lastName: true,
        changeReason: true
      }}
      initialTouched={{
        email: true,
        firstName: true,
        lastName: true,
        phoneNumber: true,
        phoneNumberCountryCode: true,
        jobTitle: true,
        providedOrganizationName: true
      }}
      requiredFields={{
        email: true,
        firstName: true,
        lastName: true,
        phoneNumber: true,
        phoneNumberCountryCode: true,
        jobTitle: true
      }}
    >
      {({ values, isValid, setValues, errors, validateForm, dirty }) => {
        const onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void = e => {
          const checked = e.target.checked
          setContactConsentConfirmed(checked)
        }

        const handleSelectContact: (contact: ContactParsed) => void = (contact) => {
          setSelectedContactId(contact.id)
          setContactConsentConfirmed(false)
          setValues(contact).catch(error => {
            console.error('Error setting values:', error)
          })
        }

        const createNewContact: () => void = () => {
          if (!isNil(onCreateNewContact)) {
            onCreateNewContact()
          }
        }

        const confirmSelection: () => void = () => {
          if (!isNil(onConfirmSelection)) {
            onConfirmSelection({ contact: values, isValid })
          }
        }

        if (loading) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <Loading isNotFixed />
            </Box>
          )
        }

        return (
          <>
            <Box
              sx={{
                marginTop: '18px',
                marginBottom: '48px',
                fontWeight: 'bold',
                fontSize: 24,
                lineHeight: '28px',
                color: 'primary.main',
                textAlign: 'center'
              }}
            >
              {type === 'project-affiliations' && !isNil(role)
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <Trans>PROJECT_AFFILIATIONS_ADD_TITLE</Trans>
                    <Box>{role}</Box>
                  </Box>)
                : <Trans>ADD_LEGAL_CONTACT_TITLE</Trans>}
            </Box>
            <Typography
              variant='h2'
              sx={{
                fontSize: 20,
                lineHeight: '25px',
                fontWeight: 700,
                paddingBottom: '32px',
                color: 'primary.main'
              }}
            >
              {isSingleResult
                ? <Trans>SEARCH_RESULTS_TITLE</Trans>
                : <Trans>MULTIPLE_RESULTS_TITLE</Trans>}
            </Typography>
            <Typography
              variant='h6'
              style={{
                fontSize: 12,
                lineHeight: '16px',
                fontWeight: 600
              }}
            >
              *<Trans>REQUIRED_ASTERIX_DESCRIPTION</Trans>
            </Typography>
            {isResults && (searchResult).map((contact, index) => {
              return (
                <ContactSearchResultCard
                  key={index}
                  contact={contact}
                  onSelect={handleSelectContact}
                  isSelected={contact.id === selectedContactId}
                  isSingleResult={isSingleResult}
                  sx={{
                    marginTop: '16px',
                    marginBottom: isSingleResult ? 0 : '16px',
                    cursor: isSingleResult ? 'default' : 'pointer'
                  }}
                />
              )
            })}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', paddingTop: '16px', paddingBottom: '10px', gap: '2px', marginLeft: 0, marginBottom: 0 }}
              control={
                <Checkbox
                  checked={contactConsentConfirmed}
                  onChange={onCheckboxChange}
                  disabled={isNil(selectedContactId)}
                  size='small'
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: '20px',
                    paddingTop: '4px'
                  }}
                >
                  {'* '}<Trans>CONTACT_CONSENT_CHECKBOX_DESCRIPTION</Trans>
                </Typography>
              }
            />

            <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px', marginTop: '22px' }}>
              <Button
                disabled={
                  loading ||
                  isNil(selectedContactId) ||
                  !contactConsentConfirmed
                }
                sx={{ flex: 1 }}
                color='primary'
                variant='contained'
                onClick={() => {
                  const contact = searchResult.find(c => c.id === selectedContactId)
                  if (!isNil(contact)) {
                    confirmSelection()
                  }
                }}
              >
                <Trans>CONFIRM_AND_ADD_CONTACT_BTN</Trans>
              </Button>
              <CustomButton
                variant='outlined'
                sx={{ height: '35px', whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => {
                  if (!isNil(onModifySearch)) {
                    onModifySearch()
                  }
                }}
              >
                <Trans>MODIFY_SEARCH_BTN</Trans>
              </CustomButton>
            </Box>
            <Button
              fullWidth
              disabled={loading || preview}
              variant='contained'
              color='primary'
              onClick={createNewContact}
            >
              <Trans>Create new contact</Trans>
            </Button>
            {/* {askForRelationship && (
              <TextFieldWithFormik
                fullWidth
                variant='outlined'
                label={<Trans>Relationship to provider</Trans>}
                name='relationship'
                style={{ marginBottom: 10, marginTop: 10 }}
                inputProps={{ maxLength: 255 }}
                helperText={countHelperText(relationship, 255)}
              />
            )} */}
          </>
        )
      }}
    </ContactFields>
  )
}
